import { registerLocaleData } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import localeIt from '@angular/common/locales/it';
import { APP_INITIALIZER, ApplicationConfig, LOCALE_ID, importProvidersFrom } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideSvgIconsConfig } from '@ngneat/svg-icon';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';

import { routes } from './app.routes';
import { DynamicImportLoader } from './i18n/dynamic-import-loader';
import { addIcon } from './svg/add';
import { addListIcon } from './svg/add-list';
import { addTaskIcon } from './svg/add-task';
import { adminPanelSettingsIcon } from './svg/admin-panel-settings';
import { altRouteIcon } from './svg/alt-route';
import { archiveIcon } from './svg/archive';
import { areaChartIcon } from './svg/area_chart';
import { arrowCircleDownIcon } from './svg/arrow-circle.down';
import { arrowLeftIcon } from './svg/arrow-left';
import { arrowRightIcon } from './svg/arrow-right';
import { attachMoneyIcon } from './svg/attach-money';
import { attachmentIcon } from './svg/attachment';
import { badgeIcon } from './svg/badge';
import { brokenImageIcon } from './svg/broken-image';
import { calendarIcon } from './svg/calendar';
import { checkIcon } from './svg/check';
import { checkIndeterminateSmallIcon } from './svg/check_indeterminate_small';
import { checkSmallIcon } from './svg/check_small';
import { checklistRtlIcon } from './svg/checklist-rtl';
import { chevronDoubleLeftIcon } from './svg/chevron-double-left';
import { chevronDownIcon } from './svg/chevron-down';
import { chevronLeftIcon } from './svg/chevron-left';
import { chevronRightIcon } from './svg/chevron-right';
import { chevronUpIcon } from './svg/chevron-up';
import { closeIcon } from './svg/close';
import { commentIcon } from './svg/comment';
import { compareArrowsIcon } from './svg/compare_arrows';
import { contentCopyIcon } from './svg/content-copy';
import { copyAllIcon } from './svg/copy-all';
import { csvIcon } from './svg/csv';
import { dashboardIcon } from './svg/dashboard';
import { dataUsageIcon } from './svg/data-usage';
import { dataThresholdingIcon } from './svg/data_thresholding';
import { deleteOutlineIcon } from './svg/delete-outline';
import { dfAttachmentIcon } from './svg/df-attachment';
import { dfCheckboxIcon } from './svg/df-checkbox';
import { dfDateIcon } from './svg/df-date';
import { dfDropdownIcon } from './svg/df-dropdown';
import { dfGroupIcon } from './svg/df-group';
import { dfNumberIcon } from './svg/df-number';
import { dfRadioIcon } from './svg/df-radio';
import { dfSectionIcon } from './svg/df-section';
import { dfTextIcon } from './svg/df-text';
import { dotIcon } from './svg/dot';
import { dynamicFormIcon } from './svg/dynamic-form';
import { editIcon } from './svg/edit';
import { effectiveBasicIcon } from './svg/effective-basic';
import { effectiveLaborIcon } from './svg/effective-labor';
import { effectiveRevenueIcon } from './svg/effective-revenue';
import { ellipsisVerticalIcon } from './svg/ellipsis-vertical';
import { estimatedBasicSubscriptionIcon } from './svg/estimated-basic-subscription';
import { estimatedBasicTantumIcon } from './svg/estimated-basic-tantum';
import { estimatedLaborIcon } from './svg/estimated-labor';
import { estimatedRevenueIcon } from './svg/estimated-revenue';
import { eventNoteIcon } from './svg/event-note';
import { fileDownloadIcon } from './svg/file-download';
import { fileUploadIcon } from './svg/file-upload';
import { filterIcon } from './svg/filter';
import { flagIcon } from './svg/flag';
import { folderIcon } from './svg/folder';
import { folderCopyIcon } from './svg/folder-copy';
import { folderSharedIcon } from './svg/folder-shared';
import { gradingIcon } from './svg/grading';
import { guastoIcon } from './svg/guasto';
import { homeIcon } from './svg/home';
import { hourglassIcon } from './svg/hourglass';
import { howToRegIcon } from './svg/how-to-reg';
import { imageIcon } from './svg/image';
import { infoIcon } from './svg/info';
import { inventoryIcon } from './svg/inventory';
import { joinFullIcon } from './svg/join_full';
import { labelIcon } from './svg/label';
import { listIcon } from './svg/list';
import { listAltIcon } from './svg/list-alt';
import { locationOnIcon } from './svg/location-on';
import { lockIcon } from './svg/lock';
import { logoutIcon } from './svg/logout';
import { mapIcon } from './svg/map';
import { menuOpenIcon } from './svg/menu-open';
import { notificationIcon } from './svg/notification';
import { openInBrowserIcon } from './svg/open-in-browser';
import { openInNewIcon } from './svg/open-in-new';
import { openInFullIcon } from './svg/open_in_full';
import { paidIcon } from './svg/paid';
import { passkeyIcon } from './svg/passkey';
import { pdfIcon } from './svg/pdf';
import { peopleIcon } from './svg/people';
import { personIcon } from './svg/person';
import { personAddIcon } from './svg/person-add';
import { personOffIcon } from './svg/person-off';
import { personPinIcon } from './svg/person-pin';
import { personRemoveIcon } from './svg/person-remove';
import { phaseIcon } from './svg/phase';
import { pinDropIcon } from './svg/pin-drop';
import { priorityHighIcon } from './svg/priority-high';
import { progressIcon } from './svg/progress';
import { publicIcon } from './svg/public';
import { pushPinIcon } from './svg/push_pin';
import { refreshIcon } from './svg/refresh';
import { routeIcon } from './svg/route';
import { searchIcon } from './svg/search';
import { settingsIcon } from './svg/settings';
import { sortIcon } from './svg/sort';
import { sortAlphabetIcon } from './svg/sort-alphabet';
import { sortDownIcon } from './svg/sort-down';
import { sortUpIcon } from './svg/sort-up';
import { starIcon } from './svg/star';
import { starOutlineIcon } from './svg/star-outline';
import { starBorderIcon } from './svg/star_border';
import { straightenIcon } from './svg/straighten';
import { summarizeIcon } from './svg/summarize';
import { supervisorAccountIcon } from './svg/supervisor-account';
import { swapVertIcon } from './svg/swap-vert';
import { swipeRightIcon } from './svg/swipe-right';
import { taskIcon } from './svg/task';
import { taskAltIcon } from './svg/task-alt';
import { templateIcon } from './svg/template';
import { timerIcon } from './svg/timer';
import { unknownIcon } from './svg/unknown';
import { viewTimelineIcon } from './svg/view-timeline';
import { visibilityIcon } from './svg/visibility';
import { visibilityOffIcon } from './svg/visibility_off';
import { warningIcon } from './svg/warning';
import { widgetsIcon } from './svg/widgets';
import { workHistoryIcon } from './svg/work-history';
import { wpBugIcon } from './svg/wp-bug';
import { wpMilestoneIcon } from './svg/wp-milestone';
import { wpPhaseIcon } from './svg/wp-phase';
import { wpPriorityHighIcon } from './svg/wp-priority-high';
import { wpPriorityHighestIcon } from './svg/wp-priority-highest';
import { wpPriorityLowIcon } from './svg/wp-priority-low';
import { wpPriorityLowestIcon } from './svg/wp-priority-lowest';
import { wpPriorityMediumIcon } from './svg/wp-priority-medium';
import { wpTaskIcon } from './svg/wp-task';
import { zoomInIcon } from './svg/zoom-in';
import { zoomOutIcon } from './svg/zoom-out';
import { environment } from '../environments/environment';
import { ApiModule } from '@api/backend-connector/api.module';

export const svgIcons = [
  addIcon,
  addListIcon,
  addTaskIcon,
  adminPanelSettingsIcon,
  altRouteIcon,
  archiveIcon,
  areaChartIcon,
  arrowCircleDownIcon,
  arrowLeftIcon,
  arrowRightIcon,
  attachmentIcon,
  attachMoneyIcon,
  badgeIcon,
  brokenImageIcon,
  calendarIcon,
  checkIcon,
  checkIndeterminateSmallIcon,
  checklistRtlIcon,
  checkSmallIcon,
  chevronDoubleLeftIcon,
  chevronDownIcon,
  chevronLeftIcon,
  chevronUpIcon,
  closeIcon,
  commentIcon,
  compareArrowsIcon,
  contentCopyIcon,
  copyAllIcon,
  csvIcon,
  dashboardIcon,
  dataThresholdingIcon,
  dataUsageIcon,
  deleteOutlineIcon,
  dfAttachmentIcon,
  dfCheckboxIcon,
  dfDateIcon,
  dfDropdownIcon,
  dfGroupIcon,
  dfNumberIcon,
  dfRadioIcon,
  dfSectionIcon,
  dfTextIcon,
  dotIcon,
  dynamicFormIcon,
  editIcon,
  ellipsisVerticalIcon,
  eventNoteIcon,
  fileDownloadIcon,
  fileUploadIcon,
  filterIcon,
  flagIcon,
  folderCopyIcon,
  folderIcon,
  folderSharedIcon,
  gradingIcon,
  guastoIcon,
  homeIcon,
  howToRegIcon,
  hourglassIcon,
  imageIcon,
  infoIcon,
  inventoryIcon,
  joinFullIcon,
  labelIcon,
  listAltIcon,
  listIcon,
  locationOnIcon,
  lockIcon,
  logoutIcon,
  mapIcon,
  menuOpenIcon,
  notificationIcon,
  openInBrowserIcon,
  openInFullIcon,
  openInNewIcon,
  paidIcon,
  passkeyIcon,
  peopleIcon,
  personAddIcon,
  personIcon,
  personOffIcon,
  personPinIcon,
  personRemoveIcon,
  pdfIcon,
  phaseIcon,
  pinDropIcon,
  priorityHighIcon,
  progressIcon,
  publicIcon,
  pushPinIcon,
  refreshIcon,
  routeIcon,
  searchIcon,
  settingsIcon,
  chevronRightIcon,
  sortAlphabetIcon,
  sortDownIcon,
  sortIcon,
  sortUpIcon,
  starBorderIcon,
  starIcon,
  starOutlineIcon,
  straightenIcon,
  summarizeIcon,
  supervisorAccountIcon,
  swapVertIcon,
  swipeRightIcon,
  taskAltIcon,
  taskIcon,
  templateIcon,
  timerIcon,
  unknownIcon,
  viewTimelineIcon,
  visibilityIcon,
  visibilityOffIcon,
  warningIcon,
  wpBugIcon,
  wpTaskIcon,
  wpMilestoneIcon,
  wpPhaseIcon,
  wpPriorityHighestIcon,
  wpPriorityHighIcon,
  wpPriorityMediumIcon,
  wpPriorityLowIcon,
  wpPriorityLowestIcon,
  widgetsIcon,
  workHistoryIcon,
  zoomInIcon,
  zoomOutIcon,
  effectiveBasicIcon,
  effectiveLaborIcon,
  effectiveRevenueIcon,
  estimatedRevenueIcon,
  estimatedLaborIcon,
  estimatedBasicSubscriptionIcon,
  estimatedBasicTantumIcon,
];

export type SvgName = (typeof svgIcons)[number]['name'];

registerLocaleData(localeIt, 'it-IT');

const setPrimeNgLocale = (primengConfig: PrimeNGConfig) => () => {
  primengConfig.setTranslation({
    dayNames: ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
    dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'],
    dayNamesMin: ['D', 'L', 'M', 'M', 'G', 'V', 'S'],
    monthNames: [
      'Gennaio',
      'Febbraio',
      'Marzo',
      'Aprile',
      'Maggio',
      'Giugno',
      'Luglio',
      'Agosto',
      'Settembre',
      'Ottobre',
      'Novembre',
      'Dicembre',
    ],
    monthNamesShort: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
    today: 'Oggi',
    clear: 'Cancella',
    firstDayOfWeek: 1,
    dateFormat: 'dd/mm/yy',
    weekHeader: 'S',
  });
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(),
    provideSvgIconsConfig({
      icons: svgIcons,
      missingIconFallback: unknownIcon,
      sizes: {
        sm: '16px',
        md: '20px',
        lg: '24px',
      },
      defaultSize: 'md',
    }),
    importProvidersFrom(
      ApiModule.forRoot({
        rootUrl: environment.apiBasePath,
      }),
      BrowserAnimationsModule,
      TranslateModule.forRoot({
        defaultLanguage: environment.defaultLang,
        loader: {
          provide: TranslateLoader,
          useClass: DynamicImportLoader,
        },
      }),
    ),
    { provide: LOCALE_ID, useValue: 'it-IT' },
    {
      provide: APP_INITIALIZER,
      useFactory: setPrimeNgLocale,
      deps: [PrimeNGConfig],
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
